import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp } from '@clerk/clerk-react';
import ProtectedRoute from '../components/ProtectedRoute';
import PageSkeleton from '../components/PageSkeleton';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';

const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const ExpensesPage = lazy(() => import('../pages/ExpensesPage'));
const CreateExpensePage = lazy(() => import('../pages/CreateExpensePage'));
const ExpenseDetailPage = lazy(() => import('../pages/ExpenseDetailPage'));
const EditExpensePage = lazy(() => import('../pages/EditExpensePage'));
const UsersPage = lazy(() => import('../pages/UsersPage'));
const GrantsPage = lazy(() => import('../pages/GrantsPage'));
const BalancesPage = lazy(() => import('../pages/BalancesPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const BulkImportPage = lazy(() => import('../pages/BulkImportPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Legacy /login redirects to Clerk sign-in */}
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />

        {/* Clerk sign-in / sign-up routes */}
        <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" forceRedirectUrl="/dashboard" />} />
        <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" signInUrl="/sign-in" forceRedirectUrl="/dashboard" />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        
        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <DashboardPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Expenses */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <ExpensesPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/new"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <CreateExpensePage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/bulk-import"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <BulkImportPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/:id"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <ExpenseDetailPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses/:id/edit"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <EditExpensePage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Users */}
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <UsersPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Grants */}
        <Route
          path="/grants"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <GrantsPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Balances */}
        <Route
          path="/balances"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <BalancesPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Settings — handles /settings, /settings/profile, /settings/api-keys */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <SettingsPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <SettingsPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/api-keys"
          element={
            <ProtectedRoute>
              <RouteErrorBoundary>
                <SettingsPage />
              </RouteErrorBoundary>
            </ProtectedRoute>
          }
        />
        
        {/* Catch-all route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
